<script setup lang="ts">
import { useHTTP } from '@/plugins/http'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { onBeforeMount, ref } from 'vue'
import { Role } from '@/models/user/Role'
import { Route } from '@/router/routes'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import AlertBadge from '@/components/AlertBadge.vue'
import RoleForm from '@/views/hr/roles/RoleForm.vue'
import { ErrorResponse } from '@/types/types'

const http = useHTTP()
const router = useRouter()
const route = useRoute()
const { t } = useI18n({
  messages: {
    en: {
      roleUpdated: 'Role was updated successfully'
    },
    ar: {
      roleUpdated: 'تم تعديل المنصب بنجاح'
    }
  }
})

const role = ref<Role>(new Role('', '', []))

async function fetchRole(id: string) {
  try {
    loading.value = true
    const resp = await http.get<Role>(`${Role.ENDPOINT}/${id}`)
    role.value = resp
    loading.value = false
  } catch (errorResp) {
    const es = errorResp as ErrorResponse
    error.value.title = es.title
    error.value.body = es.body || ''
    loading.value = false
  }
}

onBeforeMount(async () => {
  await fetchRole(route.params.id as string)
})

const putRole = async (role: Role) => {
  try {
    await http.put<Role>(`${Role.ENDPOINT}/${role.id}`, role)
  } catch (errorResp) {
    return errorResp
  } finally {
    await router.push(Route.HR_ROLES)
  }
}

const loading = ref(false)
const error = ref({ title: '', body: '' })
</script>

<template>
  <div v-if="loading" class="flex h-full justify-center items-center">
    <loading-spinner diameter="64px" />
  </div>

  <alert-badge
    v-else-if="error.title"
    isDanger
    :title="error.title"
    :body="error.body"
    @dismissed="
      () => {
        error.title = ''
        error.body = ''
      }
    "
  />

  <role-form v-else :submit-action="putRole" :init-role="role" :success-msg="t('roleUpdated')" />
</template>

<style scoped></style>
